import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  MouseEvent,
  FC
} from 'react';
import { Virtuoso } from 'react-virtuoso';
import groupSchema from 'jsUtilities/getGroupSchema';
import Loader from 'components/loader';
import styles from './index.module.css';
import { fetchBulkRequests } from 'jsApi/CommandApi';
import { accessTo } from 'jsModels/Access';
import BulkArchived from 'jsViews/bulk_archived';
import TaskListItem from './task_list_item';
import KanbanCard from '../card';
import { getCurrentProjectTags } from 'models/Project';
import { ProjectProvider } from '../../../../../clients/providers/Project';
import BulkActionsBar from '../bulk_actions_bar';
import {
  BulkAction,
  SELECT_TASK_GROUP,
  SELECT_TASK_RANGE,
  getMultiSelectList,
  wasMultiSelectKeyUsed,
  wasToggleInSelectionGroupKeyUsed
} from '../multiSelect/Util';
import { MAX_NUMBER_OF_TASKS } from '../Cards';
import { AntdWrapper } from '../../../../clients/shared/AntdWrapper';
import { CurrentUserProvider } from '../../../../../clients/providers/CurrentUser';
import usePusherSubscribe from '../../../hooks/usePusherSubscribe';
import { Event, Subscription } from '../../../models/Pusher';
import { EmptyState, TaskListView } from './EmptyState';
import cx from 'classnames';
import { ListHeader, FeedbackViewMode } from './ListHeader';
import { FEEDBACK_CARD_VIEW_EXPERIMENT } from '../../../../../clients/providers/Organization';

const VIEW_MODE_STORAGE_KEY = 'bugherd_feedback_view_mode';
const CARD_SCALE_STORAGE_KEY = 'bugherd_feedback_card_scale';
const CARD_SCALE_MIN = 200;
const CARD_SCALE_MAX = 420;
const CARD_SCALE_DEFAULT = 260;
const CARD_GRID_GAP = 12;
const CARD_GRID_PADDING = 30;
const FEEDBACK_CARD_VIEW_EXPERIMENT = 'feedback_card_view';

const safeSetItem = (key: string, value: string) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {}
};

const getStoredViewMode = (): FeedbackViewMode => {
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) ===
      FeedbackViewMode.Card
      ? FeedbackViewMode.Card
      : FeedbackViewMode.List;
  } catch {
    return FeedbackViewMode.List;
  }
};

const getStoredCardScale = (): number => {
  try {
    const stored = Number(window.localStorage.getItem(CARD_SCALE_STORAGE_KEY));
    return stored >= CARD_SCALE_MIN && stored <= CARD_SCALE_MAX
      ? stored
      : CARD_SCALE_DEFAULT;
  } catch {
    return CARD_SCALE_DEFAULT;
  }
};

const noop = () => {};

const getAssignees = (taskId: number) => {
  const task = (window as any).bugherd?.application?.tasksCollection?.get(
    taskId
  );
  return task ? task.getAssignees().map((user: any) => user.toJSON()) : [];
};

const ScrollSpacer = () => <div className={styles.scrollSpacer} />;
const virtuosoComponents = { Footer: ScrollSpacer };

type CardGridRow =
  | { type: 'header'; title: string }
  | { type: 'row'; tasks: any[] };

type Props = {
  collection: any;
  moveToTaskBoard: (id: number) => void;
  activeTask: number;
  sortAttribute: string;
  sortAscending: boolean;
  isAdminView: boolean;
  isArchiveViewActive: boolean;
  getContainer: HTMLDivElement;
  bulkActions?: any;
  closeDetails?: () => void;
  handleTaskClick: (taskId: string) => void;
  users?: {
    get: (key: number) => any;
    models: any[];
  };
  taskEditStatus: boolean;
  projectId: number;
  pusherApiKey: string;
  pusherChannelName: string;
  isGuest: boolean;
  subscription?: Subscription;
  currentUserId: number;
  loadFullArchive?: () => void;
  fullArchive?: boolean;
  searchLoading?: boolean;
  searchTaskIds?: number[] | null;
  experiments?: string[];
};

const TaskList: FC<Props> = props => {
  const {
    collection,
    moveToTaskBoard,
    activeTask,
    sortAttribute,
    sortAscending,
    isAdminView,
    isArchiveViewActive,
    getContainer,
    bulkActions,
    closeDetails,
    users,
    taskEditStatus,
    projectId,
    isGuest,
    currentUserId,
    loadFullArchive,
    searchLoading = false,
    searchTaskIds,
    experiments = []
  } = props;

  const cardViewEnabled = experiments.includes(FEEDBACK_CARD_VIEW_EXPERIMENT);

  const [archiveBulkRequests, setArchiveBulkRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fullArchive, setFullArchive] = useState<boolean>(!!props.fullArchive);
  const [selectedTaskList, setSelectedTaskList] = useState<string[]>([]);
  const [storedViewMode, setViewMode] = useState<FeedbackViewMode>(
    getStoredViewMode
  );
  const viewMode = cardViewEnabled ? storedViewMode : FeedbackViewMode.List;
  const [cardScale, setCardScale] = useState<number>(getStoredCardScale);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [gridContainerWidth, setGridContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (viewMode !== FeedbackViewMode.Card || !gridContainerRef.current) {
      return undefined;
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setGridContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(gridContainerRef.current);

    return () => observer.disconnect();
  }, [viewMode]);

  const handleViewModeChange = (mode: FeedbackViewMode) => {
    setViewMode(mode);
    safeSetItem(VIEW_MODE_STORAGE_KEY, mode);
  };

  const handleCardScaleChange = (scale: number) => {
    setCardScale(scale);
    safeSetItem(CARD_SCALE_STORAGE_KEY, scale.toString());
  };

  const fetchCompleted = useCallback(() => {
    setLoading(true);
    fetchBulkRequests({
      projectId: projectId,
      status: 'completed',
      objectAction: 'archive',
      relatedBulkRequestId: ''
    }).then(payload => {
      setArchiveBulkRequests(payload.data);
      setLoading(false);
    });
  }, [projectId]);

  useEffect(() => {
    if (isArchiveViewActive) {
      fetchCompleted();
    }
  }, [isArchiveViewActive, fetchCompleted]);

  const unSelectAll = () => {
    if (selectedTaskList.length > 0) {
      setSelectedTaskList([]);
    }
  };

  const onWindowClick = event => {
    if (event.defaultPrevented) return;
    unSelectAll();
  };

  const onWindowKeyDown = event => {
    if (event.defaultPrevented) return;
    if (event.key === 'Escape') unSelectAll();

    if (
      activeTask &&
      selectedTaskList.length < 1 &&
      (wasMultiSelectKeyUsed(event) || wasToggleInSelectionGroupKeyUsed(event))
    ) {
      setSelectedTaskList([activeTask.toString()]);
    }
  };

  useEffect(() => {
    window.addEventListener('click', onWindowClick);
    window.addEventListener('keydown', onWindowKeyDown);

    return () => {
      window.removeEventListener('click', onWindowClick);
      window.removeEventListener('keydown', onWindowKeyDown);
    };
  }, []);

  const _collection = collection.filter(
    task =>
      task.isVisible() &&
      (searchTaskIds === null ||
        searchTaskIds === undefined ||
        searchTaskIds.includes(task.id))
  );

  const collectionClone = _collection.map(task => task.toJSON());

  const groupedTasks = () => {
    const schema = groupSchema(_collection, sortAttribute).find(s =>
      s.attributes.includes(sortAttribute)
    );
    // @ts-expect-error
    const { grouper } = schema;
    // @ts-expect-error
    const groups = JSON.parse(JSON.stringify(schema.groups)); // deep clone array
    collectionClone
      .filter(task => task.id)
      .forEach(task => {
        for (var i = 0; i < groups.length; i++) {
          const group = groups[i];
          const addToGroup = grouper(group, task);

          if (addToGroup) {
            group.tasks.push(task);
            break;
          }
        }
      });

    return groups;
  };

  const sortedTasks = groupedTasks();

  if (!sortAscending) {
    sortedTasks.reverse();
  }

  const displayOrderTaskIds = sortedTasks
    .filter(group => group.tasks.length)
    .flatMap(group => group.tasks)
    .map(task => task.id.toString());

  const multiSelect = (taskId: string, action: BulkAction) => {
    const selectedList = selectedTaskList;
    const columnTaskIdList = displayOrderTaskIds;

    if (selectedList.length > MAX_NUMBER_OF_TASKS) {
      setSelectedTaskList(selectedTaskList.slice(0, MAX_NUMBER_OF_TASKS));
    }

    if (
      selectedList.length !== 0 &&
      selectedList.length < MAX_NUMBER_OF_TASKS
    ) {
      const selectedIdList = getMultiSelectList(
        action,
        taskId.toString(),
        selectedList,
        columnTaskIdList
      );
      setSelectedTaskList([...selectedIdList]);
    } else if (selectedList.length < MAX_NUMBER_OF_TASKS) {
      setSelectedTaskList([...selectedTaskList, taskId]);
    }
  };

  const handleTaskClick = (event: MouseEvent | InputEvent, taskId: string) => {
    event.preventDefault();
    // @ts-expect-error
    const isCheckboxChecked = event.target?.checked;

    if (!isCheckboxChecked && selectedTaskList.length < 2) {
      props.handleTaskClick(taskId);
    }

    //TODO: when a task is open and you click on shift you should be able to multiselect as well, this works on the task board
    //Probably have to look at the onWindowKeyDown on line 66 too

    if (wasMultiSelectKeyUsed(event)) {
      multiSelect(taskId.toString(), SELECT_TASK_RANGE);
    } else if (wasToggleInSelectionGroupKeyUsed(event) || isCheckboxChecked) {
      setSelectedTaskList([...selectedTaskList, taskId]);
      multiSelect(taskId.toString(), SELECT_TASK_GROUP);
    } else if (!isCheckboxChecked) {
      const withoutRemovedTask = selectedTaskList.filter(
        currentTaskId => currentTaskId !== taskId
      );
      setSelectedTaskList(withoutRemovedTask);
    } else {
      unSelectAll();
    }
  };

  // limiting the number of multiple selections cause we dont know what the effect will be when we bulk update a tonne of tasks
  useEffect(() => {
    const selectedTotal = selectedTaskList.length;
    if (selectedTotal > MAX_NUMBER_OF_TASKS) {
      setSelectedTaskList([
        ...selectedTaskList.slice(0, MAX_NUMBER_OF_TASKS)
      ]);
    }
    if (selectedTotal > 1) {
      closeDetails?.();
    }
  }, [selectedTaskList]);

  const isSelected = (taskId: string): boolean =>
    selectedTaskList.includes(taskId);

  const showBulkArchived =
    archiveBulkRequests.length > 0 && isArchiveViewActive && taskEditStatus;

  const taskActive = selectedTaskList.length >= 1;

  const sortedTasksList = sortedTasks
    .filter(group => group.tasks.length)
    .flatMap(group => [{ type: 'header', title: group.title }, ...group.tasks]);

  const projectTags = getCurrentProjectTags() || {};

  const cardColumns = useMemo(() => {
    const availableWidth = gridContainerWidth - CARD_GRID_PADDING;
    if (availableWidth <= 0) return 1;
    return Math.max(
      1,
      Math.floor((availableWidth + CARD_GRID_GAP) / (cardScale + CARD_GRID_GAP))
    );
  }, [gridContainerWidth, cardScale]);

  const cardGridRows = useMemo(() => {
    const rows: CardGridRow[] = [];
    let currentRow: any[] = [];

    const flushRow = () => {
      if (currentRow.length) {
        rows.push({ type: 'row', tasks: currentRow });
        currentRow = [];
      }
    };

    sortedTasksList.forEach(entry => {
      if (entry.type === 'header') {
        flushRow();
        rows.push({ type: 'header', title: entry.title });
        return;
      }

      currentRow.push(entry);
      if (currentRow.length === cardColumns) {
        flushRow();
      }
    });
    flushRow();

    return rows;
  }, [sortedTasksList, cardColumns]);

  const renderGroupHeader = (title: string) => (
    <div className={styles.separatorOuter} key={title}>
      <div className={styles.listSeparator} key={title}>
        <span className={styles.separatorName}>{title}</span>
      </div>
    </div>
  );

  const renderGridCard = (data: any) => {
    const isTaskActive = activeTask === data.id;
    return (
      <KanbanCard
        {...{
          key: data.id,
          id: data.id,
          local_task_id: data.local_task_id,
          title: data.title,
          site: data.site,
          url: data.url,
          description: data.description,
          description_html: data.description_html,
          ai_suggested_title: data.ai_suggested_title,
          original_copy_text: data.original_copy_text,
          priority_id: data.priority_id,
          due_at: data.due_at,
          tags: projectTags,
          tag_ids: data.tag_ids,
          asset_id: data.asset_id,
          asset_type: data.asset_type,
          related_to_task_ids: data.related_to_task_ids,
          comments: data.comments,
          hasScreenRecording:
            !!data.screenshot_url?.endsWith('.mp4') ||
            !!data.screenshot_url?.endsWith('.webm'),
          guestVisible: !!data.guest_visible,
          viewGuestKanban: accessTo('view_guest_kanban'),
          action: '',
          canDrag: false,
          dragHandleProps: { style: { margin: 0, width: '100%' } },
          isActive: isTaskActive || isSelected(data.id?.toString()),
          setActiveTask: (id: number) => props.handleTaskClick(id.toString()),
          moveTaskToFeedback: noop,
          moveTaskToArchive: noop,
          getAssignees,
          multiSelect: (taskId: string, action: string) =>
            multiSelect(taskId, action as BulkAction),
          unSelectAll,
          selectedTaskList
        }}
      />
    );
  };

  const renderCardRow = (index: number, row: CardGridRow) => {
    if (row.type === 'header') {
      return renderGroupHeader(row.title);
    }

    return (
      <div className={styles.cardGridRow}>
        {row.tasks.map(task => (
          <div
            className={styles.cardGridCell}
            style={{ width: cardScale }}
            key={task.id}
          >
            {renderGridCard(task)}
          </div>
        ))}
      </div>
    );
  };

  const renderCard = (index: number, data) => {
    if (data.type === 'header') {
      return renderGroupHeader(data.title);
    }

    return (
      <TaskListItem
        {...{
          key: data.id,
          task: data,
          index,
          taskEditStatus,
          handleTaskClick,
          moveToTaskBoard,
          isAdminView,
          activeTaskId: activeTask,
          taskActive,
          getContainer,
          isSelected: isSelected(data.id?.toString()),
          // @ts-expect-error
          assignedUsers: users
            //@ts-expect-error
            .filter(user => data.assignee_ids.includes(user.get('id')))
            .map(user => ({ ...user.toJSON(), role: user.get('role') }))
            .filter(
              ({ role, id }) =>
                !isGuest ||
                (accessTo('assign_guests_allowed') &&
                  accessTo('assign_guests') &&
                  ((data.guest_visible && role === 'guest') ||
                    currentUserId === id))
            )
        }}
      />
    );
  };

  return (
    <div
      className={cx(styles.taskListOuter, {
        [styles.empty]: !searchLoading && !sortedTasksList.length
      })}
      aria-busy={searchLoading}
    >
      <ListHeader
        onClick={() => {
          setFullArchive(true);
          if (loadFullArchive) loadFullArchive();
        }}
        fullArchive={fullArchive}
        isArchiveViewActive={isArchiveViewActive}
        cardViewEnabled={cardViewEnabled}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        cardScale={cardScale}
        onCardScaleChange={handleCardScaleChange}
        cardScaleMin={CARD_SCALE_MIN}
        cardScaleMax={CARD_SCALE_MAX}
      />
      {isAdminView && (
        <BulkActionsBar
          setSelectedTasks={setSelectedTaskList}
          selectedTasks={selectedTaskList}
          getContainer={
            typeof getContainer === 'function'
              ? (getContainer as () => HTMLDivElement)
              : () => getContainer as HTMLDivElement
          }
          onClose={unSelectAll}
          bulkActions={bulkActions ?? []}
          setActiveTask={id => props.handleTaskClick(id.toString())}
          permissions={{
            editTaskStatus: taskEditStatus,
            editTaskAssignees: accessTo('task_edit_assignees'),
            editTaskDueDate: accessTo('task_edit_due_date'),
            editTaskTags: accessTo('task_edit_tags'),
            editTaskSeverity: accessTo('task_edit_severity'),
            billingRights: accessTo('billing'),
            taskEditVisibility: accessTo('task_edit_visibility'),
            taskMoveToProject: accessTo('task_move_to_project'),
            taskDelete: accessTo('task_delete'),
            taskMerge: accessTo('task_merge')
          }}
        />
      )}
      {showBulkArchived && (
        <BulkArchived
          {...{
            singleRequest: archiveBulkRequests.length === 1,
            getContainer,
            loading,
            setLoading,
            archiveBulkRequests,
            fetchCompleted
          }}
        />
      )}
      {searchLoading ? (
        <div
          className={styles.searchLoadingIndicator}
          role="status"
          aria-label="Searching tasks"
        >
          <Loader size="large" />
        </div>
      ) : sortedTasksList.length ? (
        viewMode === FeedbackViewMode.Card ? (
          <div className={styles.cardGridOuter} ref={gridContainerRef}>
            {gridContainerWidth > 0 && (
              <Virtuoso
                data={cardGridRows}
                totalCount={cardGridRows.length}
                itemContent={renderCardRow}
                components={virtuosoComponents}
              />
            )}
          </div>
        ) : (
          <Virtuoso
            data={sortedTasksList}
            totalCount={sortedTasksList.length}
            itemContent={renderCard}
            components={virtuosoComponents}
          />
        )
      ) : (
        <EmptyState
          viewGuestKanban={accessTo('view_guest_kanban')}
          manageProject={accessTo('manage_project')}
          guestsSeeGuestsAllowed={accessTo('guests_see_guests_allowed')}
          guestsSeeGuestsEnabled={accessTo('guests_see_guests')}
          view={
            isArchiveViewActive ? TaskListView.Archive : TaskListView.Feedback
          }
        />
      )}
    </div>
  );
};

export default (props: Props) => {
  const {
    pusherApiKey,
    pusherChannelName,
    subscription = Subscription.TRIAGE,
    taskEditStatus: editTaskStatus,
    projectId,
    isGuest
  } = props;
  const [taskEditStatus, setTaskEditStatus] = useState<boolean>(editTaskStatus);

  const handleGuestEditTaskStatusUpdate = (
    isGuestUser: boolean,
    pusherData: any
  ) => {
    if (isGuestUser) {
      setTaskEditStatus(pusherData.guest_edit_task_status);
    }
  };

  usePusherSubscribe({
    pusher: {
      config: {
        pusherApiKey,
        pusherChannelAuthEndpoint: '/pusher/auth'
      },
      projects: [{ id: String(projectId), pusherChannelName }],
      events: [
        {
          name: Event.GUEST_EDIT_STATUS_UPDATED,
          onUpdate: handleGuestEditTaskStatusUpdate.bind(null, isGuest)
        }
      ],
      subscription
    },
    loading: false
  });

  return (
    <AntdWrapper>
      <CurrentUserProvider apiDomain="">
        <ProjectProvider id={`${props.projectId}`} apiDomain="">
          <TaskList {...{ ...props, taskEditStatus }} />
        </ProjectProvider>
      </CurrentUserProvider>
    </AntdWrapper>
  );
};
